import { useState } from 'react';

import { useEvent } from '@mint-lib/hooks';
import { useTranslation } from '@mint-lib/i18n';
import { useQuery } from '@mint-lib/query-client';
import {
  Button,
  CircularLoadingButton,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerRoot,
  DrawerTitle,
} from '@mint-lib/tw-components';

import {
  getFileAccessOption,
  useUpdateFileAccessMutation,
} from '../../../queries/fileAccess.js';
import type {
  FileAccess,
  FileAccessType,
  FileAccessUser,
} from '../../../types/rag.js';
import { ShareFilesSection } from './ShareFilesSection.jsx';

type FieldsProps = {
  fileId: string;
  initialAccess: FileAccess;
  onClose: () => void;
};

const MobileEditPermissionsFields = ({
  fileId,
  initialAccess,
  onClose,
}: FieldsProps) => {
  const { t } = useTranslation('@mint-lib/ai-chat');
  const { mutate: updateFileAccess, isPending } = useUpdateFileAccessMutation();

  const [accessType, setAccessType] = useState<FileAccessType>(
    initialAccess.accessType,
  );
  const [sharedUsers, setSharedUsers] = useState<FileAccessUser[]>(
    initialAccess.users,
  );

  const handleSave = useEvent(() => {
    updateFileAccess(
      { fileId, access: { accessType, users: sharedUsers } },
      { onSuccess: onClose },
    );
  });

  return (
    <>
      {/* No onClose here: the header's close button is a Radix Close, which
          already drives the drawer's onOpenChange. Passing it too would call
          back twice per dismissal. */}
      <DrawerHeader>
        <DrawerTitle>{t('Edit permissions')}</DrawerTitle>
        <Button
          variant="contained"
          size="large"
          onClick={handleSave}
          disabled={isPending}
          className="ml-auto"
        >
          {isPending && (
            <CircularLoadingButton size="small" variant="outlined" />
          )}
          {t('Save')}
        </Button>
      </DrawerHeader>
      <DrawerContent className="flex-col gap-4 overflow-y-auto">
        <ShareFilesSection
          accessType={accessType}
          onAccessTypeChange={setAccessType}
          sharedUsers={sharedUsers}
          onSharedUsersChange={setSharedUsers}
        />
      </DrawerContent>
    </>
  );
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  fileId: string;
};

const MobileEditPermissionsDrawer = ({ isOpen, onClose, fileId }: Props) => {
  const { data: fileAccess } = useQuery({
    ...getFileAccessOption(fileId),
    enabled: isOpen,
  });

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DrawerPortal>
        <DrawerOverlay />
        <DrawerRoot
          side="bottom"
          aria-describedby={undefined}
          // Hugs its content, but the access list grows with every shared user,
          // so cap it and let the content scroll instead of running off screen.
          className="max-h-[90%] pb-3 data-[side=bottom]:h-auto"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {/* Closing unmounts the fields, so every open starts from the saved
              access rather than a leftover draft. */}
          <MobileEditPermissionsFields
            key={fileId}
            fileId={fileId}
            initialAccess={fileAccess}
            onClose={onClose}
          />
        </DrawerRoot>
      </DrawerPortal>
    </Drawer>
  );
};

export { MobileEditPermissionsDrawer };
